import { useState } from "react";
import { FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errorMessage } from "@/lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

export function FilesPanel({ quoteId, files, items = [], onChanged }) {
  const [target, setTarget] = useState(null);
  const [withLines, setWithLines] = useState(true);
  const only = target ? items.filter((i) => (i.source_file_ids || []).length === 1 && i.source_file_ids[0] === target.file_id) : [];
  const edited = only.filter((i) => i.user_edited).length;
  const open = async (f) => {
    try {
      const r = await api.get(`/drawings/${f.file_id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async () => {
    const f = target;
    setTarget(null);
    try {
      const { data } = await api.delete(`/quotes/${quoteId}/files/${f.file_id}`, { params: { remove_lines: withLines } });
      onChanged(data.quote);
      toast.success(`Removed ${f.filename}${withLines ? ` and ${data.removed_lines} line${data.removed_lines === 1 ? "" : "s"}` : ""}`);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  if (!files?.length) return null;
  return (
    <section data-testid="quote-files-panel" className="rounded-2xl border border-slate-800 bg-[#F2F2F7]/60">
      <p className="border-b border-slate-800 px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Files ({files.length})</p>
      <ul className="divide-y divide-slate-800">
        {files.map((f, i) => (
          <li key={`${f.file_id}-${i}`} data-testid={`quote-file-${i}`} className="flex items-center gap-3 px-5 py-2.5">
            <button data-testid={`quote-file-open-${i}`} onClick={() => open(f)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
              <FileText className="h-4 w-4 shrink-0 text-amber-300" />
              <span className="truncate text-sm text-slate-100 hover:underline">{f.filename}</span>
              {f.label && <span data-testid={`file-label-${i}`} className="shrink-0 rounded bg-sky-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">{f.label}</span>}
            </button>
            <span data-testid={`quote-file-meta-${i}`} className="shrink-0 text-xs text-slate-500">
              {when(f.added_at)} · {f.status === "failed" ? "Couldn't read" : `${f.items_found} items`}
              {f.read_pages && <span data-testid={`quote-file-read-pages-${i}`} className="block text-[11px] text-sky-300/80">{f.read_pages}</span>}
            </span>
            <button data-testid={`quote-file-remove-${i}`} onClick={() => { setWithLines(true); setTarget(f); }} title="Remove file"
              className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent className="border-slate-800 bg-[#F2F2F7]">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {target?.filename}?</AlertDialogTitle>
            <AlertDialogDescription>The file is detached from this quote.</AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-center gap-2 text-sm text-slate-200">
            <Checkbox data-testid="remove-file-lines-checkbox" checked={withLines} onCheckedChange={(v) => setWithLines(!!v)} />
            Also remove the {only.length} line{only.length === 1 ? "" : "s"} that came only from this file
          </label>
          {withLines && edited > 0 && (
            <p data-testid="remove-file-edited-warning" className="text-xs text-orange-300">{edited} of them {edited === 1 ? "was" : "were"} edited by you.</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-file-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="remove-file-confirm" onClick={remove} className="bg-red-600 hover:bg-red-500">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
